import { useCallback, useEffect, useMemo } from 'react';

import get from 'lodash-es/get';
import { CircleHelpIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ToolkitEditor } from '@/components/organisms/Toolkit/ToolkitEditor/ToolkitEditor';
import { FormField } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import useFocusOnInput from '@/hooks/useFocusOnInput';
import useWebhooks from '@/hooks/useWebhooks';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { WebhookAction } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { LENGTH_XXL } from '@/util/validation/constants';
import { charCountValidation, errorMessage } from '@/util/validation/helpers';

import ToolkitWrapper from '../../ToolkitWrapper';

type WebhookActionForm = { webhook_id: string; textValue: string };

const ToolkitWebhook = () => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { getContextVariables } = useDialogs(brainId);
  const variables = useMemo(() => getContextVariables(), [getContextVariables]);

  const dispatch = useDispatch();
  const { actionWebhooks } = useWebhooks(brainId);
  const { type, actionId, webhook_id, fallback } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as WebhookAction;
    const text = get(selectedAction, 'fallback[0].texts[0]', '');
    return {
      type: selectedAction?.type,
      actionId: selectedAction?.action_id,
      webhook_id: selectedAction?.webhook_id,
      fallback: text ?? '',
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  const options = useMemo(() => {
    return actionWebhooks?.map((w) => ({ value: w.webhook_id, label: w.name }));
  }, [actionWebhooks]);

  const {
    formState: { errors },
    getValues,
    setValue,
    control,
    trigger,
    setFocus,
  } = useForm<WebhookActionForm>({
    mode: 'onChange',
    defaultValues: { webhook_id: webhook_id, textValue: fallback },
  });
  useFocusOnInput('webhook_id', errors, setFocus);
  useEffect(() => {
    if (webhook_id) {
      setValue('webhook_id', webhook_id);
    }
  }, [setValue, webhook_id]);

  // Re-sync form when action changes (e.g., switching between actions or after async load)
  useEffect(() => {
    setValue('textValue', fallback);
  }, [actionId, fallback, setValue]);

  const handleChange = useCallback(() => {
    const values = getValues() as WebhookActionForm;

    dispatch(
      updateAction({
        actionId,
        action: {
          webhook_id: values.webhook_id,
          fallback: values.textValue ? [{ type: 'text', texts: [values.textValue] }] : [],
        },
      })
    );
  }, [actionId, dispatch, getValues]);

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.webhook'),
    type,
  });

  useEffect(() => {
    setFieldError('fallback', errors?.textValue?.message);
  }, [errors?.textValue?.message, setFieldError]);

  useEffect(() => {
    // Trigger validation on mount
    trigger();
  }, [trigger]);
  return (
    <ToolkitWrapper type={type}>
      <div className="mb-6">
        <Label className="mb-2 block">{t('field.webhook_name')}</Label>
        <FormField
          control={control}
          name="webhook_id"
          rules={{ required: errorMessage({ field: { type: 'required' } }) }}
          render={({ field }) => (
            <Select
              value={field.value || undefined}
              onValueChange={(value) => {
                field.onChange(value);
                handleChange();
              }}
            >
              <SelectTrigger className={errors?.webhook_id ? 'border-destructive' : ''}>
                <SelectValue placeholder={t('dialog.select_a_webhook')} />
              </SelectTrigger>
              <SelectContent>
                {options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors?.webhook_id?.message && (
          <p className="mt-1 text-sm text-destructive">{errors?.webhook_id?.message}</p>
        )}
      </div>
      <Controller
        name="textValue"
        control={control}
        rules={{
          validate: {
            charsValidation: (v) => charCountValidation<WebhookActionForm>(v, LENGTH_XXL, t, true),
          },
        }}
        render={({ field }) => (
          <div>
            <Label className="mb-2 flex items-center gap-1 text-xs text-foreground-muted uppercase">
              {t('dialog.fallback')}
              <InfoTooltip content={t('dialog.webhook.fallback_tooltip')} side="right">
                <CircleHelpIcon size={12} className="text-foreground-muted" />
              </InfoTooltip>
            </Label>
            <ToolkitEditor
              content={field.value}
              onContentChange={(content) => {
                field.onChange(content);
                handleChange();
              }}
              variableOptions={variables}
              maxLength={LENGTH_XXL}
              placeholder={t('dialog.fallback')}
            />
            {errors[field.name]?.message && (
              <p className="mt-1 text-sm text-destructive">{errors[field.name]?.message}</p>
            )}
          </div>
        )}
      />
    </ToolkitWrapper>
  );
};

export default ToolkitWebhook;
